/**
 * Quick Pro build configurator.
 *
 * Drives the controls rendered by snippets/quickpro-configurator.liquid:
 *   - native-style radio button groups and their hidden property inputs
 *   - conditional visibility, so a frameset's drivetrain list only appears once
 *     a groupset is chosen and the cockpit sizing only once a bar is chosen
 *
 * Both catalogues are generated by build-quickpro-catalog.cjs; the rules here
 * evaluate the same `showWhen` the generator baked into the initial markup, so
 * first paint is already correct and this only maintains it from then on.
 *
 * Uses its own `data-qp-value` hook rather than the Winspace `data-crank-value`
 * one: variant-picker calls stopImmediatePropagation() on capture for that
 * selector, which would swallow these clicks.
 */
(function quickproConfigurator() {
  'use strict';

  var SETUP = '[data-quickpro-setup]';

  function groups(setup) {
    return Array.prototype.slice.call(setup.querySelectorAll('[data-qp-group]'));
  }

  /** The value a group currently submits. */
  function valueOf(group) {
    var input = group.querySelector('[data-qp-input]');
    return input ? input.value || '' : '';
  }

  function setActive(option) {
    var group = option.closest('[data-qp-group]');
    var choices = option.closest('.quickpro-setup__choices');
    if (!group || !choices) return;

    var radio = option.matches('[data-qp-choice]')
      ? option
      : option.previousElementSibling?.matches('[data-qp-choice]')
        ? option.previousElementSibling
        : null;
    if (!radio || radio.disabled) return;
    radio.checked = true;

    choices.querySelectorAll('.qp-opt[data-qp-value]').forEach(function (candidate) {
      var candidateRadio = candidate.previousElementSibling;
      var isActive = candidateRadio === radio;
      candidate.classList.toggle('ws-crank-opt--active', isActive);
    });

    var input = group.querySelector('[data-qp-input]');
    if (input) input.value = radio.value || '';
    group.classList.remove('is-invalid');
    group.removeAttribute('aria-invalid');

    refresh(option.closest(SETUP));
  }

  /**
   * `data-qp-when` encodes one condition set, in the same delimited form the
   * Liquid catalogue uses:
   *
   *   match ; fieldId @ EQUALS|NOT_EQUALS @ value ; ...
   *
   * `match` is `all` or `any`. Comparands were normalised by the generator to
   * the exact strings the controls hold, so this is a plain string compare.
   */
  function passes(group, byId, visible) {
    var raw = group.getAttribute('data-qp-when');
    if (!raw) return true;

    var parts = raw.split(';');
    var match = parts.shift();
    if (!parts.length) return true;

    var results = parts.map(function (clause) {
      var bits = clause.split('@');
      var target = byId[bits[0]];
      // A condition pointing at a group that was never rendered (its only paid
      // values lost their supplements) can never be satisfied — stay hidden
      // rather than exposing a control whose dependency the shopper cannot set.
      if (!target) return false;
      // Same rule for a group that IS rendered but is not currently shown: a
      // hidden group keeps its last answer, and that stale value must not go on
      // satisfying anything. A GR:ONE frameset chains these — the power-meter
      // list only appears for one groupset, and its crank-size list only for a
      // Magene value — so without this, choosing GRX 2x12 + Magene and then
      // switching to SRAM RED left 'Medida del potenciómetro' visible, enabled
      // and submitting a crank size for a build with no Magene power meter.
      // Fail closed in BOTH directions: with no answerable dependency,
      // NOT_EQUALS must not pass on a stale value either.
      if (visible && visible[bits[0]] === false) return false;
      var current = valueOf(target);
      return bits[1] === 'NOT_EQUALS' ? current !== bits[2] : current === bits[2];
    });

    return match === 'any' ? results.some(Boolean) : results.every(Boolean);
  }

  /**
   * Visibility is chained (a group's driver can itself be conditional), so one
   * pass is not enough — hiding a driver has to propagate to whatever depends on
   * it. Start from "everything shows" and switch groups off until the result
   * stops changing: a group can only ever lose visibility as drivers disappear,
   * so this settles, and it settles on the largest consistent set rather than
   * hiding anything it need not.
   */
  function resolveVisibility(all, byId) {
    var visible = {};
    all.forEach(function (group) { visible[group.getAttribute('data-qp-group')] = true; });

    for (var pass = 0; pass <= all.length; pass += 1) {
      var changed = false;
      all.forEach(function (group) {
        var id = group.getAttribute('data-qp-group');
        if (visible[id] === false) return;
        if (!passes(group, byId, visible)) {
          visible[id] = false;
          changed = true;
        }
      });
      if (!changed) break;
    }
    return visible;
  }

  // Bottom-bracket bore follows the chosen crank axle, not simply the frame.
  // TEO's 29 mm kit is not asserted to fit either listed ceramic SKU; the
  // supplier must confirm that pairing before it is offered as an upgrade.
  function compatibleBottomBrackets(all, visible) {
    var selectedGroup = all.find(function (g) { return g.dataset.qpProperty === 'Grupo'; });
    var meter = all.find(function (g) {
      return g.dataset.qpProperty === 'Potenciómetro' && visible[g.dataset.qpGroup] !== false;
    });
    var pm = meter ? valueOf(meter) : '';
    all.filter(function (g) { return g.dataset.qpProperty === 'Eje de pedalier'; }).forEach(function (g) {
      var family = g.dataset.qpCrankFamily || 'frame';
      var gs = selectedGroup ? valueOf(selectedGroup) : '';
      if (selectedGroup) family = /SRAM/i.test(gs) ? 'sram' : /Shimano/i.test(gs) ? 'shimano' : 'frame';
      if (/Magene PES/i.test(pm)) family = 'shimano';
      if (/Magene TEO/i.test(pm)) family = 'unconfirmed';
      var choices = Array.prototype.slice.call(g.querySelectorAll('[data-qp-choice]'));
      choices.forEach(function (radio) {
        var incompatible = radio.hasAttribute('data-qp-supplement-variant') &&
          (family === 'unconfirmed' || (family !== 'frame' && radio.value.toLowerCase().indexOf(family) === -1));
        radio.hidden = incompatible;
        radio.disabled = incompatible || visible[g.dataset.qpGroup] === false;
        var label = radio.nextElementSibling;
        if (label) label.hidden = incompatible;
        if (incompatible) radio.checked = false;
      });
      // Drop the stale paid selection and return to the free default.
      var active = choices.find(function (r) { return r.checked && !r.hidden; });
      if (!active) active = choices.find(function (r) { return !r.hidden && !r.hasAttribute('data-qp-supplement-variant'); });
      if (active) active.checked = true;
      g.querySelector('[data-qp-input]').value = active ? active.value : '';
      choices.forEach(function (r) {
        if (r.nextElementSibling) r.nextElementSibling.classList.toggle('ws-crank-opt--active', r === active);
      });
    });
  }

  /**
   * A Magene power meter replaces the groupset's cranks, and the QED version its
   * chainrings too, so its size is not a second, free choice: it is whatever the
   * drivetrain says. Offering both let a shopper order a 172,5 mm drivetrain
   * with a 170 mm meter. The drivetrain stays the one control — sizes the meter
   * is not made in are withdrawn from it — and the meter's size group is filled
   * in from it, out of sight but still submitted, so the order records both.
   */
  function crankOf(value) {
    var m = /^\s*([\d.,]+)\s*mm/i.exec(value || '');
    return m ? m[1].replace(',', '.') : '';
  }

  function ringsOf(value) {
    var m = /(\d+\/\d+)\s*T/i.exec(value || '');
    return m ? m[1] : '';
  }

  function check(group, radio) {
    var choices = Array.prototype.slice.call(group.querySelectorAll('[data-qp-choice]'));
    choices.forEach(function (r) {
      r.checked = r === radio;
      if (r.nextElementSibling) r.nextElementSibling.classList.toggle('ws-crank-opt--active', r === radio);
    });
    var input = group.querySelector('[data-qp-input]');
    if (input) input.value = radio ? radio.value : '';
  }

  function alignPowerMeterWithDrivetrain(setup, all, visible) {
    function shown(g) { return visible[g.dataset.qpGroup] !== false; }

    // Undo the previous pass: whatever this function withdrew comes back, and
    // the note and derived flags are rebuilt from scratch.
    setup.querySelectorAll('[data-qp-pm-withdrawn]').forEach(function (radio) {
      radio.removeAttribute('data-qp-pm-withdrawn');
      radio.hidden = false;
      if (radio.nextElementSibling) radio.nextElementSibling.hidden = false;
    });
    setup.querySelectorAll('[data-qp-pm-note]').forEach(function (note) { note.remove(); });
    all.forEach(function (g) { g.removeAttribute('data-qp-derived'); });

    var drivetrain = all.find(function (g) { return g.dataset.qpProperty === 'Transmisión' && shown(g); });
    var sizeGroup = all.find(function (g) {
      return shown(g) && (g.dataset.qpProperty === 'Medida del potenciómetro' || g.dataset.qpProperty === 'Longitud de biela');
    });
    // No drivetrain on show (a frameset without a groupset): the meter's own
    // size list is the only place to choose, so it stays as it is.
    if (!drivetrain || !sizeGroup) return;

    var withRings = sizeGroup.dataset.qpProperty === 'Medida del potenciómetro';
    var sizes = Array.prototype.slice.call(sizeGroup.querySelectorAll('[data-qp-choice]')).map(function (radio) {
      return { radio: radio, crank: crankOf(radio.value), rings: ringsOf(radio.value) };
    });
    function fit(value) {
      return sizes.find(function (size) {
        return size.crank && size.crank === crankOf(value) && (!withRings || size.rings === ringsOf(value));
      });
    }

    var choices = Array.prototype.slice.call(drivetrain.querySelectorAll('[data-qp-choice]'));
    var current = valueOf(drivetrain);
    choices.forEach(function (radio) {
      if (fit(radio.value)) return;
      radio.setAttribute('data-qp-pm-withdrawn', '');
      radio.hidden = true;
      radio.checked = false;
      if (radio.nextElementSibling) radio.nextElementSibling.hidden = true;
    });

    // When their crank length had to go, keep the shopper's chainrings and move
    // to the nearest length the meter is made in (172,5 -> 170, not 165).
    var available = choices.filter(function (r) { return !r.hidden; });
    function distance(r) { return Math.abs(Number(crankOf(r.value)) - Number(crankOf(current))); }
    var sameRings = available.filter(function (r) { return ringsOf(r.value) === ringsOf(current); })
      .sort(function (x, y) { return distance(x) - distance(y); });
    var active = available.find(function (r) { return r.value === current; })
      || sameRings[0]
      || available[0];
    check(drivetrain, active || null);

    var size = active ? fit(active.value) : null;
    check(sizeGroup, size ? size.radio : null);
    sizeGroup.hidden = true;
    sizeGroup.setAttribute('data-qp-derived', '');

    var template = withRings ? setup.dataset.qpTPmCranksRings : setup.dataset.qpTPmCranks;
    if (!template) return;
    var lengths = sizes.map(function (s) { return s.crank.replace('.', ','); })
      .filter(function (len, i, list) { return len && list.indexOf(len) === i; });
    var note = document.createElement('p');
    note.className = 'quickpro-setup__note';
    note.setAttribute('data-qp-pm-note', '');
    note.textContent = template.replace('__LENGTHS__', lengths.join(' / ') + ' mm');
    drivetrain.querySelector('.quickpro-setup__choices').insertAdjacentElement('afterend', note);
  }

  /**
   * A hidden group is also DISABLED. Hiding alone would keep submitting its
   * value, and several groups share one property name — a frameset carries six
   * mutually exclusive `properties[Transmisión]` lists, so more than one enabled
   * at a time would put two conflicting drivetrains on the same order line.
   */
  function refresh(setup) {
    if (!setup) return;

    var all = groups(setup);
    var byId = {};
    all.forEach(function (group) {
      byId[group.getAttribute('data-qp-group')] = group;

      // Fail closed if the generated default was not rendered (for example a
      // retail supplement suppressed for B2B). The submitted hidden property
      // must always agree with an actual visible choice.
      var input = group.querySelector('[data-qp-input]');
      var selected = group.querySelector('[data-qp-choice]:checked');
      if (input && (!selected || selected.value !== input.value)) {
        input.value = selected ? selected.value : '';
      }
    });

    var visible = resolveVisibility(all, byId);
    all.forEach(function (group) {
      var shown = visible[group.getAttribute('data-qp-group')] !== false;
      group.hidden = !shown;
      group.querySelectorAll('select, input').forEach(function (control) {
        control.disabled = !shown;
      });
    });

    compatibleBottomBrackets(all, visible);
    alignPowerMeterWithDrivetrain(setup, all, visible);
    summarise(setup);
  }


  /* ------------------------------------------------------------- summary --- */

  /**
   * Money formatting follows the shopper's locale via Intl rather than a
   * hard-coded separator, so 1300 reads "1.300 €" in Spanish and "€1,300" in
   * English without a second format string to keep in step.
   */
  function money(cents, currency, lang) {
    var amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(lang || 'es', {
        style: 'currency',
        currency: currency || 'EUR',
        // 'always' matters: Spanish ICU omits the thousands separator below
        // 10000, which would print "1300 €" here while Shopify's money filter
        // prints "1.300 €" on the option right above it.
        useGrouping: 'always',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + (currency || 'EUR');
    }
  }

  /** Keep the primary product price in step with the configured build total. */
  function syncProductPrice(setup, base, upgrades, currency, lang) {
    var root = setup.closest('.js-product') || setup.closest('.shopify-section') || document;
    var priceSource = root.querySelector('[data-public-variant-price-source]');
    if (!priceSource) return;

    var currentValue = priceSource.querySelector('.price__current .js-value');
    if (currentValue) currentValue.textContent = money(base + upgrades, currency, lang);

    var compareAt = Number(priceSource.dataset.compareAtPrice || 0);
    var compareValue = priceSource.querySelector('.price__was .js-value');
    if (compareValue && compareAt > base) {
      compareValue.textContent = money(compareAt + upgrades, currency, lang);
    }

    priceSource.dataset.raceitAddonAdjustment = String(upgrades);
    priceSource.classList.toggle('product-info__price--configured', upgrades > 0);
  }

  /** The chosen visible option element of a group. */
  function chosen(group) {
    var input = group.querySelector('input[data-qp-input]');
    if (!input) return null;
    var active = group.querySelector('[data-qp-choice]:checked');
    // Trust the hidden input: it is what submits, the highlight is decoration.
    return active && active.value === input.value ? active : null;
  }

  /**
   * The group's own heading. quickpro-configurator.liquid renders it as a bare
   * `<legend class="label">`; the `.ws-agile-props__hd .label` wrapper belongs to
   * the Winspace crank picker in variant-picker.liquid and is never emitted here,
   * so matching on it alone left every summary row with an empty key.
   */
  function labelOf(group) {
    var el = group.querySelector('legend.label, .ws-agile-props__hd .label, .label');
    return el ? el.textContent.replace(/\s*\*\s*$/, '').trim() : '';
  }

  /**
   * What the shopper READS for a group's current answer.
   *
   * `valueOf` returns the hidden input, which is deliberately the Spanish value
   * in every locale so orders read identically — printing that in the summary
   * showed "Sin retroceso" to an English shopper. The translated string rides on
   * the radio as `data-qp-display`; fall back to the submitted value if a
   * catalogue entry ever lacks one.
   */
  function displayOf(group, value) {
    var checked = group.querySelector('[data-qp-choice]:checked');
    if (checked && checked.value === value) {
      var shown = checked.getAttribute('data-qp-display');
      if (shown) return shown;
    }
    return value;
  }

  function row(key, value, extra) {
    var wrap = document.createElement('div');
    wrap.className = 'quickpro-summary__row';

    var k = document.createElement('span');
    k.className = 'quickpro-summary__key';
    k.textContent = key;

    var v = document.createElement('span');
    v.className = 'quickpro-summary__val';
    v.textContent = value;
    if (extra) {
      var plus = document.createElement('span');
      plus.className = 'quickpro-summary__plus';
      plus.textContent = extra;
      v.appendChild(plus);
    }

    wrap.appendChild(k);
    wrap.appendChild(v);
    return wrap;
  }

  function line(key, value, isTotal) {
    var el = document.createElement('div');
    el.className = 'quickpro-summary__line' + (isTotal ? ' quickpro-summary__line--total' : '');
    var k = document.createElement('span');
    k.textContent = key;
    var v = document.createElement('span');
    v.textContent = value;
    el.appendChild(k);
    el.appendChild(v);
    return el;
  }

  /**
   * Rebuild the summary from the controls themselves — never from the catalogue
   * — so it cannot drift from what the form will actually submit.
   *
   * It stays hidden behind a hint until every REQUIRED group has an answer,
   * because a half-filled summary showing a total is worse than none.
   */
  function summarise(setup) {
    var panel = setup.querySelector('[data-qp-summary]');
    if (!panel) return;

    var list = panel.querySelector('[data-qp-summary-list]');
    var totals = panel.querySelector('[data-qp-summary-totals]');
    var hint = panel.querySelector('[data-qp-summary-hint]');
    var currency = panel.dataset.qpCurrency;
    var lang = (document.documentElement.lang || 'es').slice(0, 2).toLowerCase();

    var rows = [];
    var upgrades = 0;
    var complete = true;

    groups(setup).forEach(function (group) {
      // A derived group (the power meter's size) is hidden but still submits,
      // so the summary lists it like the order will.
      if (group.hidden && !group.hasAttribute('data-qp-derived')) return;
      var control = group.querySelector('input[data-qp-input]');
      if (!control || control.disabled) return;

      var value = valueOf(group);
      var isRequired = group.hasAttribute('data-qp-required');
      if (!value) {
        if (isRequired) complete = false;
        return;
      }

      var option = chosen(group);
      var cents = option && option.dataset.qpSupplementPrice;
      if (cents) upgrades += Number(cents);
      rows.push({ key: labelOf(group), value: displayOf(group, value), extra: cents ? '+' + money(cents, currency, lang) : '' });
    });

    // Custom paint (snippets/custom-paint-addon.liquid) sits outside the setup
    // but is part of the build the cart will charge, so it counts here too.
    var paint = customPaint(setup);
    var paintCents = paint ? Number(paint.dataset.customPaintPrice || 0) : 0;

    if (!complete || !rows.length) {
      list.hidden = true;
      totals.hidden = true;
      hint.hidden = false;
      syncProductPrice(setup, Number(panel.dataset.qpBase || 0), paintCents, currency, lang);
      return;
    }

    if (paintCents) {
      upgrades += paintCents;
      rows.push({ key: paint.dataset.customPaintLabel || '', value: paint.dataset.customPaintDisplay || paint.dataset.customPaintValue || '✓', extra: '+' + money(paintCents, currency, lang) });
    }

    // The fitted handlebar is spec, not a choice, but it belongs in a summary of
    // what the customer is getting.
    var fitted = setup.querySelector('.quickpro-fixed');
    if (fitted) {
      rows.push({
        key: fitted.querySelector('.quickpro-fixed__label').textContent.trim(),
        value: fitted.querySelector('.quickpro-fixed__value').textContent.trim(),
        extra: ''
      });
    }

    list.textContent = '';
    rows.forEach(function (r) { list.appendChild(row(r.key, r.value, r.extra)); });

    var base = Number(panel.dataset.qpBase || 0);
    var productRoot = setup.closest('.js-product') || setup.closest('.shopify-section') || document;
    var extraOptions = productRoot.querySelector('raceit-product-options');
    var genericExtras = Number(extraOptions && extraOptions.dataset.extraPrice || 0);
    totals.textContent = '';
    totals.appendChild(line(panel.dataset.qpLabelBase, money(base, currency, lang)));
    if (upgrades + genericExtras) totals.appendChild(line(panel.dataset.qpLabelUpgrades, '+' + money(upgrades + genericExtras, currency, lang)));
    totals.appendChild(line(panel.dataset.qpLabelTotal, money(base + upgrades + genericExtras, currency, lang), true));
    syncProductPrice(setup, base, upgrades, currency, lang);

    hint.hidden = true;
    list.hidden = false;
    totals.hidden = false;
  }

  /** The checked custom-paint box belonging to this setup's product, if any. */
  function customPaint(setup) {
    var root = setup.closest('.js-product') || setup.closest('.shopify-section') || document;
    return root.querySelector('[data-custom-paint]:checked:not([disabled])');
  }

  function refreshAll() {
    document.querySelectorAll(SETUP).forEach(refresh);
  }

  document.addEventListener('click', function (event) {
    var option = event.target.closest ? event.target.closest('.qp-opt[data-qp-value]') : null;
    if (option) setActive(option);
  });

  document.addEventListener('change', function (event) {
    var paint = event.target.closest ? event.target.closest('[data-custom-paint]') : null;
    if (paint) {
      var paintRoot = paint.closest('.js-product') || paint.closest('.shopify-section') || document;
      paintRoot.querySelectorAll(SETUP).forEach(summarise);
      return;
    }
    var setup = event.target.closest ? event.target.closest(SETUP) : null;
    if (!setup) return;
    var choice = event.target.closest('[data-qp-choice]');
    if (choice) {
      setActive(choice);
      return;
    }
    refresh(setup);
  });

  // Changing a real variant option re-renders the section, so re-bind after it.
  document.addEventListener('on:variant:change', refreshAll);
  document.addEventListener('raceit:options:change', function (event) {
    var root = event.target.closest('.js-product') || event.target.closest('.shopify-section');
    if (root) root.querySelectorAll(SETUP).forEach(summarise);
  });
  document.addEventListener('shopify:section:load', refreshAll);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAll);
  } else {
    refreshAll();
  }
}());
